import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, MessageSquare } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [role, setRole] = useState("guest");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadConvs = async (r) => {
    const { data } = await api.get("/conversations", { params: { role: r } });
    setConvs(data);
    if (data.length && !active) openConv(data[0]);
    if (!data.length) { setActive(null); setMessages([]); }
  };

  const openConv = async (c) => {
    setActive(c);
    const { data } = await api.get(`/conversations/${c.id}/messages`);
    setMessages(data.messages);
  };

  useEffect(() => { setActive(null); loadConvs(role); /* eslint-disable-next-line */ }, [role]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const { data } = await api.post("/messages/reply", { conversation_id: active.id, text });
    setMessages((m) => [...m, data]);
    setText("");
    loadConvs(role);
  };

  const otherName = (c) => (role === "guest" ? c.host_name : c.guest_name);

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Mesaje</h1>
          {user?.is_host && (
            <Tabs value={role} onValueChange={setRole}>
              <TabsList data-testid="messages-role-tabs">
                <TabsTrigger value="guest" data-testid="messages-tab-guest">Ca oaspete</TabsTrigger>
                <TabsTrigger value="host" data-testid="messages-tab-host">Ca gazdă</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 rounded-2xl border border-border overflow-hidden bg-card min-h-[540px]">
          {/* Conversation list */}
          <div className="border-r border-border overflow-y-auto max-h-[540px]">
            {convs.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                Nicio conversație încă.
              </div>
            ) : convs.map((c) => (
              <button key={c.id} onClick={() => openConv(c)} data-testid={`conversation-${c.id}`}
                      className={`w-full text-left px-4 py-4 border-b border-border/60 flex gap-3 transition-colors ${active?.id === c.id ? "bg-muted" : "hover:bg-muted/50"}`}>
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">{otherName(c)?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{otherName(c)}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.property_title}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Message stream */}
          <div className="md:col-span-2 flex flex-col max-h-[540px]">
            {active ? (
              <>
                <div className="px-5 py-3 border-b border-border">
                  <p className="font-medium">{otherName(active)}</p>
                  <p className="text-xs text-muted-foreground">{active.property_title}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.user_id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-accent text-accent-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <div className="p-4 border-t border-border flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input-field"
                         onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrie un mesaj..." className="rounded-full" />
                  <Button onClick={send} data-testid="message-send-button" size="icon"
                          className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground shrink-0">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Selectează o conversație
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
